import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Newspaper, Calendar, Eye, User, Search, Filter,
  Clock, ArrowRight, Star, TrendingUp, Briefcase, 
  Bitcoin, Building, Loader2, ChevronLeft, ChevronRight
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category icons and colors
const CATEGORIES = {
  general: { label: 'Général', icon: Newspaper, color: 'bg-blue-500/20 text-blue-500' },
  market: { label: 'Marchés', icon: TrendingUp, color: 'bg-green-500/20 text-green-500' },
  crypto: { label: 'Crypto', icon: Bitcoin, color: 'bg-orange-500/20 text-orange-500' },
  investment: { label: 'Investissement', icon: Briefcase, color: 'bg-purple-500/20 text-purple-500' },
  company: { label: 'Entreprise', icon: Building, color: 'bg-gray-500/20 text-gray-400' },
};

export default function ClientNews() {
  const { token } = useAuth();
  const { currentTemplate } = useTheme();
  const [searchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [page, setPage] = useState(1);
  const [totalArticles, setTotalArticles] = useState(0);
  const articlesPerPage = 9;

  const primaryColor = currentTemplate?.colors?.primary || '#0052FF';

  useEffect(() => {
    fetchArticles();
  }, [selectedCategory, page]);

  useEffect(() => {
    fetchFeaturedArticles();
  }, []);

  // Open article from URL param (from dashboard featured news click)
  useEffect(() => {
    const articleId = searchParams.get('article');
    if (articleId && articles.length > 0) {
      const found = articles.find(a => a.id === articleId) || featuredArticles.find(a => a.id === articleId);
      if (found) {
        setSelectedArticle(found);
        setShowArticleModal(true);
      } else {
        // Fetch the specific article
        axios.get(`${API}/news/${articleId}`, { headers: { Authorization: `Bearer ${token}` } })
          .then(res => { setSelectedArticle(res.data); setShowArticleModal(true); })
          .catch(() => {});
      }
    }
  }, [searchParams, articles, featuredArticles]);

  const fetchArticles = async () => {
    try {
      const params = new URLSearchParams({
        limit: articlesPerPage.toString(),
        skip: ((page - 1) * articlesPerPage).toString()
      });
      
      if (selectedCategory !== 'all') {
        params.append('category', selectedCategory);
      }
      
      const response = await axios.get(`${API}/news?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setArticles(response.data.articles || []);
      setTotalArticles(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeaturedArticles = async () => {
    try {
      const response = await axios.get(`${API}/news?featured=true&limit=3`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeaturedArticles(response.data.articles || []);
    } catch (error) {
      console.error('Error fetching featured articles:', error);
    }
  };

  const openArticle = async (article) => {
    try {
      const response = await axios.get(`${API}/news/${article.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedArticle(response.data);
      setShowArticleModal(true);
    } catch (error) {
      toast.error("Erreur lors du chargement de l'article");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const filteredArticles = articles.filter(article =>
    article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.summary?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(totalArticles / articlesPerPage);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Newspaper className="w-7 h-7" style={{ color: primaryColor }} />
              Actualités
            </h1>
            <p className="text-muted-foreground mt-1">
              Restez informé des dernières nouvelles financières
            </p>
          </div>
        </div>

        {/* Featured Articles */}
        {featuredArticles.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              À la une
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {featuredArticles.map((article, index) => (
                <Card 
                  key={article.id}
                  className={`bg-card/50 backdrop-blur border-border/50 cursor-pointer hover:border-primary/50 transition-all overflow-hidden ${index === 0 ? 'md:col-span-2 md:row-span-2' : ''}`}
                  onClick={() => openArticle(article)}
                >
                  {article.cover_image && (
                    <div className={`relative ${index === 0 ? 'h-48 md:h-64' : 'h-32'}`}>
                      <img 
                        src={article.cover_image} 
                        alt={article.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                      <div className="absolute bottom-3 left-3 right-3">
                        <Badge className={CATEGORIES[article.category]?.color || CATEGORIES.general.color}>
                          {CATEGORIES[article.category]?.label || article.category}
                        </Badge>
                      </div>
                    </div>
                  )}
                  <CardContent className="p-4">
                    <h3 className={`font-semibold text-white mb-2 line-clamp-2 ${index === 0 ? 'text-xl' : 'text-sm'}`}>
                      {article.title}
                    </h3>
                    {index === 0 && article.summary && (
                      <p className="text-muted-foreground text-sm line-clamp-2 mb-3">
                        {article.summary}
                      </p>
                    )}
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(article.published_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        {article.views} vues
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher un article..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-background/50"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setSelectedCategory('all'); setPage(1); }}
            >
              Tous
            </Button>
            {Object.entries(CATEGORIES).map(([key, { label, icon: Icon, color }]) => (
              <Button
                key={key}
                variant={selectedCategory === key ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setSelectedCategory(key); setPage(1); }}
                className="gap-1"
              >
                <Icon className="w-3 h-3" />
                {label}
              </Button>
            ))}
          </div>
        </div>

        {/* Articles Grid */}
        {filteredArticles.length === 0 ? (
          <Card className="bg-card/50 backdrop-blur border-border/50">
            <CardContent className="py-12 text-center">
              <Newspaper className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">Aucun article trouvé</p>
              {searchQuery && (
                <p className="text-sm text-muted-foreground mt-1">
                  Essayez de modifier votre recherche
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredArticles.map((article) => {
              const CategoryIcon = CATEGORIES[article.category]?.icon || Newspaper;
              return (
                <Card 
                  key={article.id}
                  className="bg-card/50 backdrop-blur border-border/50 cursor-pointer hover:border-primary/50 transition-all overflow-hidden group"
                  onClick={() => openArticle(article)}
                  data-testid={`article-${article.id}`}
                >
                  {article.cover_image ? (
                    <div className="relative h-40 overflow-hidden">
                      <img 
                        src={article.cover_image} 
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-2 left-2">
                        <Badge className={CATEGORIES[article.category]?.color || CATEGORIES.general.color}>
                          {CATEGORIES[article.category]?.label || article.category}
                        </Badge>
                      </div>
                    </div>
                  ) : (
                    <div className="h-40 bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                      <CategoryIcon className="w-12 h-12 text-primary/50" />
                    </div>
                  )}
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-white mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                      {article.title}
                    </h3>
                    {article.summary && (
                      <p className="text-muted-foreground text-sm line-clamp-2 mb-3">
                        {article.summary}
                      </p>
                    )}
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(article.published_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        {article.views}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page} sur {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Article Modal */}
        <Dialog open={showArticleModal} onOpenChange={setShowArticleModal}>
          <DialogContent className="bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto">
            {selectedArticle && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={CATEGORIES[selectedArticle.category]?.color || CATEGORIES.general.color}>
                      {CATEGORIES[selectedArticle.category]?.label || selectedArticle.category}
                    </Badge>
                    {selectedArticle.is_featured && (
                      <Badge className="bg-yellow-500/20 text-yellow-500">
                        <Star className="w-3 h-3 mr-1" />
                        À la une
                      </Badge>
                    )}
                  </div>
                  <DialogTitle className="text-xl text-white">
                    {selectedArticle.title}
                  </DialogTitle>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2">
                    <span className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {selectedArticle.author_name || 'Admin'}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(selectedArticle.published_at)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="w-4 h-4" />
                      {selectedArticle.views} vues
                    </span>
                  </div>
                </DialogHeader>
                
                {selectedArticle.cover_image && (
                  <div className="mt-4 rounded-lg overflow-hidden">
                    <img 
                      src={selectedArticle.cover_image} 
                      alt={selectedArticle.title}
                      className="w-full h-64 object-cover"
                    />
                  </div>
                )}
                
                {selectedArticle.summary && (
                  <p className="text-muted-foreground italic border-l-2 border-primary pl-4 my-4">
                    {selectedArticle.summary}
                  </p>
                )}
                
                <div 
                  className="prose prose-invert max-w-none mt-4 prose-headings:text-white prose-headings:font-semibold prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-3 prose-p:text-gray-300 prose-p:leading-relaxed prose-p:my-3 prose-strong:text-white prose-ul:my-3 prose-li:text-gray-300 prose-li:my-1 prose-blockquote:border-primary prose-blockquote:text-gray-400 prose-blockquote:italic"
                  dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
                />
                
                {selectedArticle.tags && selectedArticle.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t border-border">
                    {selectedArticle.tags.map((tag, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        #{tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
